import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { hasRoles, EDIT_PARTICIPANT_ROLES } from '../../../../../util/RoleUtil';
import {
  DATE_PICKER_POPPER_MODIFIERS,
  parseYMD,
  parseWindow,
  isWithinInclusive,
  sameDay,
} from '../../../../../util/DateUtil';
import { formatDateAsString } from '../../../../../util/StringUtil';
import { alphaNumericSort } from '../../../../../util/SortUtil';

import {
  viewParticipant,
  viewParticipantVisitCodes,
  populateVisitToCapture,
  changeParticipantSchedule,
  logParticipantVisit,
} from '../../../../../services/ParticipantService';
import { retrieveStudySchedulesForParticipantDropdowns } from '../../../../../services/StudyService';

import { SaveButton } from '../../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../../common/tool-tip/ToolTip';
import Modal from '../../../../common/modal/Modal';
import { ParticipantAddForms } from './ParticipantAddForms';
import MissedVisitWarning from './missed-visit-warning/MissedVisitWarning';
import MissedVisitCapture from './missed-visit-capture/MissedVisitCapture';
import AdjustVisitsWarning from './adjust-visits-warning/AdjustVisitsWarning';
import ChangeParticipantScheduleConfirmation from './ChangeParticipantScheduleConfirmation';

import './CaptureVisit.css';

export const CaptureVisit = ({
  participant,
  setParticipant,
  context,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
}) => {
  const [visitDate, setVisitDate] = useState();
  const [visitSchedule, setVisitSchedule] = useState('');
  const [visitCode, setVisitCode] = useState('');
  const [subVisitCode, setSubVisitCode] = useState('');
  const [visitType, setVisitType] = useState('');
  const [formsList, setFormsList] = useState([]);
  const [targetDate, setTargetDate] = useState();
  const [scheduleList, setScheduleList] = useState([]);
  const [newSchedule, setNewSchedule] = useState(undefined);
  const [newScheduleVisits, setNewScheduleVisits] = useState([]);
  const [startVisitCode, setStartVisitCode] = useState(undefined);
  const [visitCodeList, setVisitCodeList] = useState([]);
  const [visitTypeList, setVisitTypeList] = useState([]);
  const [newVisitCode, setNewVisitCode] = useState(undefined);
  const [newSubVisitCode, setNewSubVisitCode] = useState('');
  const [newVisitType, setNewVisitType] = useState('');
  const [isChangeVisitModalOpen, setIsChangeVisitModalOpen] = useState(false);
  const [isChangeScheduleModalOpen, setIsChangeScheduleModalOpen] = useState(false);
  const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
  const [isScheduleConfirmationOpen, setIsScheduleConfirmationOpen] = useState(false);
  const [pendingScheduleChange, setPendingScheduleChange] = useState(null);
  const [visitDateError, setVisitDateError] = useState('');
  const [targetDateError, setTargetDateError] = useState('');
  const [newScheduleError, setNewScheduleError] = useState('');
  const [startVisitError, setStartVisitError] = useState('');
  const [newVisitCodeError, setNewVisitCodeError] = useState('');
  const [newSubVisitCodeError, setNewSubVisitCodeError] = useState('');
  const [visitCodeError, setVisitCodeError] = useState('');
  const [newVisitTypeError, setNewVisitTypeError] = useState('');
  const [showMissedVisitWarning, setShowMissedVisitWarning] = useState(false);
  const [showAdjustVisitWarning, setShowAdjustVisitWarning] = useState(false);
  const [willLogVisitLater, setWillLogVisitLater] = useState(false);
  const [loggingMissedVisit, setLoggingMissedVisit] = useState(false);
  const [showMissedVisitCapture, setShowMissedVisitCapture] = useState(false);
  const [pendingVisitToLog, setPendingVisitToLog] = useState(null);

  useEffect(() => {
    setVisitSchedule(participant?.studySchedule || '');

    if (!participant?.captureVisit) return;

    if (participant.captureVisit.uncapturedVisitAlert) {
      setVisitDate(null);
      setShowMissedVisitWarning(true);
    }

    setVisitSchedule(participant?.studySchedule);
  }, [participant]);

  const retrieveParticipant = async (username, externalReference) => {
    const [data, error] = await viewParticipant(username, externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        setParticipant(data.participant);
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Participant Information Retrieved',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleChangeVisitCodeClick = () => {
    retrieveVisitCodes().then(() => {
      setIsChangeVisitModalOpen(true);
      setNewVisitCodeError('');
      setNewSubVisitCodeError('');
      setNewVisitTypeError('');
    });
  };

  const handleChangeVisitScheduleClick = () => {
    retrieveStudySchedules(participant?.externalReference, participant?.activeStudyExternalReference).then(() => {
      setIsChangeScheduleModalOpen(true);
      setTargetDateError('');
      setTargetDate('');
      setStartVisitCode('');
      setStartVisitError('');
    });
  };

  const handleAddFormsClick = () => {
    setIsAddFormModalOpen(true);
  };

  const handleLogVisitClick = () => {
    const currentDate = new Date();
    if (!visitDate) {
      setVisitDateError('*REQUIRED');
    } else if (visitDate && visitDate > currentDate) {
      setVisitDateError('Visit Date cannot be future dated.');
    } else {
      setVisitDateError('');
    }

    if (!visitCode || visitCode === '' || visitCode === 'N/A') {
      setVisitCodeError('*REQUIRED');
    } else {
      setVisitCodeError('');
    }

    const visitCodeExists = participant?.pastVisits.some((visit) => visit.visitCode === visitCode);
    if (visitCodeExists && !subVisitCode) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: 'Visit has already been logged.',
      });
      return;
    }

    if (!visitDate || (visitDate && visitDate > currentDate) || !visitCode || visitCode === 'N/A') {
      return;
    }

    const formExternalReferences = formsList.map((form) => form.formExternalReference);

    const visitToLog = {
      logVisitVO: {
        formExternalReferences: formExternalReferences,
        participantExternalReference: participant?.externalReference || '',
        subVisitCode: subVisitCode || null,
        visitCode: visitCode || '',
        visitDate: formatDateAsString(visitDate) || '',
        visitType: visitType || '',
        adjustFutureVisits: false,
      },
    };

    const matchingFutureVisit = participant?.futureVisits?.find((v) => v.visitCode === visitCode && true);

    if (matchingFutureVisit?.targetDate && matchingFutureVisit?.visitWindow) {
      const target = parseYMD(matchingFutureVisit.targetDate);
      const { start, end } = parseWindow(matchingFutureVisit.visitWindow);

      const withinWindow = isWithinInclusive(visitDate, start, end);
      const onTargetDate = target ? sameDay(visitDate, target) : false;

      if (withinWindow && !onTargetDate) {
        setPendingVisitToLog(visitToLog);
        setShowAdjustVisitWarning(true);
        return;
      }
    }

    logVisit(visitToLog).then(() => {
      setNewVisitCode(undefined);
      setNewSubVisitCode('');
      setNewVisitType('');
      setLoggingMissedVisit(false);
    });
  };

  const logVisit = async (logVisitRequest) => {
    setSpinnerText('Capturing Visit');
    const [data, error] = await logParticipantVisit(context.username, logVisitRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Visit successfully logged.',
        });
        resetCaptureVisitForm();

        await retrieveParticipant(context.username, participant.externalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const resetCaptureVisitForm = () => {
    setVisitDate(null);
    setVisitCode('');
    setSubVisitCode('');
    setVisitType('');
    setFormsList([]);
    setVisitDateError('');
    setVisitCodeError('');
    setNewVisitCode(undefined);
    setNewSubVisitCode('');
    setNewVisitType('');
    setLoggingMissedVisit(false);
    setWillLogVisitLater(false);
  };

  const changeSchedule = async (changeScheduleRequest) => {
    setSpinnerText('Updating Participant Schedule');
    const [data, error] = await changeParticipantSchedule(context.username, changeScheduleRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Schedule successfully changed.',
        });
        resetCaptureVisitForm();

        await retrieveParticipant(context.username, participant.externalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const retrieveStudySchedules = useCallback(
    async (externalReference, studyExternalReference) => {
      setSpinnerText('Retrieving Study Schedules');

      const [scheduleData, scheduleError] = await retrieveStudySchedulesForParticipantDropdowns(
        externalReference,
        studyExternalReference,
        context.username,
        false
      );

      if (scheduleData) {
        if (scheduleData.responseStatus === 'Success') {
          const participantScheduleExtRef = participant?.studyScheduleExternalReference;

          const formattedSchedules = scheduleData.dropdownStudyScheduleVOS
            .filter((schedule) => schedule.studyScheduleExternalReference !== participantScheduleExtRef)
            .map((schedule) => ({
              value: schedule,
              label: schedule.name,
            }));

          setScheduleList(formattedSchedules);
          setNewScheduleVisits([]);
        } else {
          showToaster(scheduleData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: scheduleError.message,
        });
      }

      setSpinnerText(null);
    },
    [context.username, participant, setSpinnerText, showToaster]
  );

  const retrieveVisitCodes = async () => {
    setSpinnerText('Retrieving Visit Codes');
    const findVisitCodesRequest = {
      participantExternalReference: participant.externalReference,
    };

    const [data, error] = await viewParticipantVisitCodes(context.username, findVisitCodesRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        const sortedVisitCodes = data.visitCodes.sort((objectOne, objectTwo) =>
          alphaNumericSort(objectOne, objectTwo, 'visitCode')
        );

        setVisitTypeList(
          sortedVisitCodes.map((visit) => ({
            label: visit.visitType,
            value: visit.visitType,
          }))
        );

        setVisitCodeList(
          sortedVisitCodes.map((visit) => ({
            label: visit.visitCode,
            value: visit,
          }))
        );

        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Participant Visit Codes Retrieved',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleSaveVisitChangeClick = () => {
    let visitCodeExistsInList;
    if (!newVisitCode) {
      setNewVisitCodeError('*REQUIRED');
    } else {
      setNewVisitCodeError('');
      visitCodeExistsInList = visitCodeList.some((visit) => visit.value.visitCode === newVisitCode.visitCode);

      if (visitCodeExistsInList && newVisitCode.visitCaptured && !newSubVisitCode) {
        setNewVisitCodeError('*ALREADY CAPTURED');
      } else {
        setNewVisitCodeError('');
      }

      if (newVisitCode.visitCodeHasVariants && !newSubVisitCode) {
        setNewSubVisitCodeError('*REQUIRED');
      } else if (
        newVisitCode.visitCodeHasVariants &&
        newSubVisitCode &&
        newVisitCode.visitCodeVariants.includes(newSubVisitCode)
      ) {
        setNewSubVisitCodeError('*SUB-VISIT CODE IN USE');
      } else {
        setNewSubVisitCodeError('');
      }
    }

    if (!newVisitType) {
      setNewVisitTypeError('*REQUIRED');
    } else {
      setNewVisitTypeError('');
    }

    if (
      !newVisitCode ||
      !newVisitType ||
      (visitCodeExistsInList && newVisitCode.visitCaptured && !newSubVisitCode) ||
      (newVisitCode.visitCodeHasVariants && !newSubVisitCode) ||
      (newVisitCode.visitCodeHasVariants && newSubVisitCode && newVisitCode.visitCodeVariants.includes(newSubVisitCode))
    )
      return;

    setVisitCode(newVisitCode?.visitCode);
    setSubVisitCode(newSubVisitCode);
    setVisitType(newVisitType);

    setIsChangeVisitModalOpen(false);
    setIsAddFormModalOpen(true);
  };

  const handleSaveScheduleChangeClick = () => {
    if (!newSchedule) {
      setNewScheduleError('*REQUIRED');
    } else {
      setNewScheduleError('');
    }

    if (!startVisitCode) {
      setStartVisitError('*REQUIRED');
    } else {
      setStartVisitError('');
    }

    if (!targetDate) {
      setTargetDateError('*REQUIRED');
    } else {
      setTargetDateError('');
    }

    if (!newSchedule || !startVisitCode || !targetDate) return;

    const scheduleUpdate = {
      changeParticipantScheduleVO: {
        participantExternalReference: participant?.externalReference || '',
        startVisitCode: startVisitCode?.value || null,
        studyScheduleExternalReference: newSchedule?.studyScheduleExternalReference || '',
        targetDate: formatDateAsString(targetDate) || '',
      },
    };

    setPendingScheduleChange(scheduleUpdate);
    setIsChangeScheduleModalOpen(false);
    setIsScheduleConfirmationOpen(true);
  };

  const handleConfirmScheduleChange = () => {
    setIsScheduleConfirmationOpen(false);

    changeSchedule(pendingScheduleChange).then(() => {
      setPendingScheduleChange(null);
      setNewSchedule(undefined);
      setStartVisitCode('');
      setTargetDate(undefined);
    });
  };

  const handleCancelScheduleConfirmation = () => {
    setIsScheduleConfirmationOpen(false);
    setPendingScheduleChange(null);
    setIsChangeScheduleModalOpen(true);
  };

  const handleCancelVisitChangeClick = () => {
    setIsChangeVisitModalOpen(false);
  };

  const handleCancelScheduleChangeClick = () => {
    setIsChangeScheduleModalOpen(false);
  };

  const closeFormsModal = () => {
    setIsAddFormModalOpen(false);
  };

  const handleLinkVisitCodeToChange = (visitCode) => {
    setNewVisitCode(visitCode.value);
    setNewVisitType(visitCode.value.visitType);
    setNewVisitCodeError('');
    setNewVisitTypeError('');
    setNewSubVisitCode('');
  };

  const handleLinkVisitScheduleToChange = (scheduleOption) => {
    const selectedSchedule = scheduleOption?.value;

    setNewSchedule(selectedSchedule);
    setNewScheduleError('');

    const visits = (selectedSchedule?.visitCodes ?? []).map((v) => ({
      value: v.visitCode,
      label: v.visitCode.value,
    }));

    setNewScheduleVisits(visits);
  };

  const handleLinkStartVisitChange = (scheduleOption) => {
    setStartVisitCode(scheduleOption);
    setStartVisitError('');
  };

  const handleVisitTypeChange = (selectedOption) => {
    if (selectedOption === null) {
      setNewVisitType(null);
    } else {
      setNewVisitType(selectedOption.value);
      setNewVisitTypeError('');
    }
  };

  const retrieveVisitForDate = async (date) => {
    setSpinnerText('Retrieving visit');

    const [data, error] = await populateVisitToCapture(
      context.username,
      participant.externalReference,
      formatDateAsString(date)
    );

    if (data) {
      const { captureVisitVO, responseStatus } = data;
      if (responseStatus === 'Success' && captureVisitVO) {
        setSpinnerText(null);
        return captureVisitVO;
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleDateChange = async (selectedDate) => {
    setVisitDate(selectedDate);
    setVisitDateError('');

    if (loggingMissedVisit) return;

    const visitData = await retrieveVisitForDate(selectedDate);

    if (!visitData) {
      setVisitCode('');
      setSubVisitCode('');
      setVisitType('');
      setFormsList([]);
      return;
    }

    setVisitCode(visitData.visitCode);
    setSubVisitCode(visitData.subVisitCode);
    setVisitType(visitData.visitType);
    setFormsList(visitData.forms);
  };

  const handleTargetDateChange = async (selectedDate) => {
    setTargetDate(selectedDate);
    setTargetDateError('');
  };

  const handleLogMissedVisitClick = () => {
    setShowMissedVisitWarning(false);
    setShowMissedVisitCapture(true);
  };

  const handleLogVisitLaterClick = () => {
    setWillLogVisitLater(true);
    setShowMissedVisitWarning(false);
  };

  const handleLogVisitNowClick = () => {
    setShowMissedVisitWarning(false);
    setLoggingMissedVisit(true);

    const missedVisit = participant?.captureVisit;

    if (missedVisit) {
      setVisitSchedule(missedVisit.visitSchedule || '');
      setVisitCode(missedVisit.visitCode || '');
      setSubVisitCode(missedVisit.subVisitCode || '');
      setVisitType(missedVisit.visitType || '');
      setFormsList(missedVisit.forms || []);
    }
  };

  const handleCancelMissedVisitClick = () => {
    setShowMissedVisitWarning(true);
    setShowMissedVisitCapture(false);
  };

  const handleCancelAdjustVisitClick = () => {
    setShowAdjustVisitWarning(false);
    setPendingVisitToLog(null);
  };

  const submitPendingLogVisit = async (adjustFutureVisits) => {
    if (!pendingVisitToLog?.logVisitVO) return;

    const updated = {
      ...pendingVisitToLog,
      logVisitVO: {
        ...pendingVisitToLog.logVisitVO,
        adjustFutureVisits,
      },
    };

    setShowAdjustVisitWarning(false);
    setPendingVisitToLog(null);

    await logVisit(updated);

    setNewVisitCode(undefined);
    setNewSubVisitCode('');
    setNewVisitType('');
    setLoggingMissedVisit(false);
  };

  const handleAdjustVisitsClick = () => {
    submitPendingLogVisit(true);
  };

  const handleKeepVisitsClick = () => {
    submitPendingLogVisit(false);
  };

  const fieldsDisabled = willLogVisitLater || !hasRoles(context, EDIT_PARTICIPANT_ROLES);

  return (
    <div className="capture-visit">
      <div className="capture-visit-content">
        <div className="input-container">
          <label className="input-label">Visit Schedule: </label>
          <input
            className={'text-input'}
            type="text"
            disabled
            value={visitSchedule || 'N/A'}
            onChange={({ target }) => {
              setVisitSchedule(target.value);
            }}
          />
          <SaveButton
            textColor={'#ffffff'}
            border={'1px solid #74a47f'}
            title={'Change'}
            onClick={handleChangeVisitScheduleClick}
            color={'#74a47f'}
            disabled={fieldsDisabled}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'captureVisitCode123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Visit Code:'}
            />
          )}
        </div>
        <div className="input-container" style={{ marginTop: '5px' }}>
          <label className="input-label">Visit Date: </label>
          <DatePicker
            selected={visitDate}
            className={`text-input ${visitDateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            disabled={fieldsDisabled}
            onChange={handleDateChange}
            popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'captureVisitDate123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Visit Date:'}
            />
          )}
        </div>
        <div className="protocol-error-message">
          {visitDateError && <div className="small-red-text">{visitDateError}</div>}
        </div>
        <div className="input-container">
          <label className="input-label">Visit Code: </label>
          <input
            className={`text-input ${visitCodeError && 'error-border'}`}
            type="text"
            disabled
            value={visitCode || 'N/A'}
            onChange={({ target }) => {
              setVisitCode(target.value);
            }}
          />
          <SaveButton
            textColor={'#ffffff'}
            border={'1px solid #74a47f'}
            title={'Change'}
            onClick={handleChangeVisitCodeClick}
            color={'#74a47f'}
            disabled={fieldsDisabled}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'captureVisitCode123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Visit Code:'}
            />
          )}
        </div>
        <div className="protocol-error-message">
          {visitCodeError && <div className="small-red-text">{visitCodeError}</div>}
        </div>
        <div />
        <div className="input-container">
          <label className="input-label">Sub-Visit Code: </label>
          <input
            className="text-input"
            type="text"
            disabled
            value={subVisitCode || 'N/A'}
            onChange={({ target }) => {
              setSubVisitCode(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'captureSubVisitCode123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Sub-Visit Code:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className="input-label">Visit Type: </label>
          <input
            className="text-input"
            type="text"
            disabled
            value={visitType || 'N/A'}
            onChange={({ target }) => {
              setVisitType(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'captureVisitType123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Visit Type:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className="input-label">Forms: </label>
          <textarea
            className="input-area-list"
            rows={formsList?.length}
            value={formsList?.map((form) => form.name).join('\n')}
            disabled
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'captureVisitForms123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Forms:'}
            />
          )}
        </div>
        <div className="capture-visit-add-button-container">
          <SaveButton
            textColor={'#ffffff'}
            border={'1px solid #74a47f'}
            title={'Add'}
            onClick={handleAddFormsClick}
            color={'#74a47f'}
            disabled={fieldsDisabled}
          />
        </div>
      </div>

      <div className={'button-container'}>
        <SaveButton
          textColor={'#ffffff'}
          border={'1px solid #74a47f'}
          title={'Log Visit'}
          onClick={handleLogVisitClick}
          color={'#74a47f'}
          disabled={fieldsDisabled}
        />
      </div>

      {isChangeScheduleModalOpen && (
        <Modal
          closeModal={() => setIsChangeScheduleModalOpen(false)}
          setIsModalOpen={setIsChangeScheduleModalOpen}
          hideButton={true}
          headerTitle={'Change Schedule'}
          width={'500px'}
          height={'300px'}
          justifyContent={'center'}
          contentPadding={'2px'}
        >
          <div className="capture-visit-add-visit-modal">
            <div className="input-container" style={{ marginTop: '10px' }}>
              <label className={'input-label'}>New Schedule:</label>
              <Select
                options={scheduleList}
                closeMenuOnSelect={true}
                onChange={handleLinkVisitScheduleToChange}
                value={scheduleList.find((option) => option.value === newSchedule)}
                classNamePrefix={`input-select${newScheduleError ? 'error-border' : ''}`}
                getOptionLabel={(option) => option?.value?.name}
                getOptionValue={(option) => option?.value}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'captureNewSched123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'New Schedule:'}
                />
              )}
            </div>
            <div className="error-message">
              {newScheduleError && <div className="small-red-text">{newScheduleError}</div>}
            </div>
            <div className="input-container">
              <label className={'input-label'}>Start Visit Code:</label>
              <Select
                options={newScheduleVisits}
                closeMenuOnSelect={true}
                onChange={handleLinkStartVisitChange}
                value={newScheduleVisits.find((option) => option.value === startVisitCode)}
                classNamePrefix={`input-select${startVisitError ? 'error-border' : ''}`}
                getOptionLabel={(option) => option?.value}
                getOptionValue={(option) => option?.value}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'captureSchedStartVisit123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Start Visit Code:'}
                />
              )}
            </div>
            <div className="error-message">
              {startVisitError && <div className="small-red-text">{startVisitError}</div>}
            </div>
            <div className="input-container" style={{ marginTop: '5px' }}>
              <label className="input-label">Target Date: </label>

              <div className="date-picker-input-wrapper">
                <DatePicker
                  selected={targetDate}
                  className={`text-input date-picker-input ${targetDateError && 'error-border'}`}
                  dateFormat="dd-MM-yyyy"
                  showPopperArrow={false}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  placeholderText="Select..."
                  disabled={fieldsDisabled}
                  onChange={handleTargetDateChange}
                  popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
                />
                <FontAwesomeIcon icon={faCalendarAlt} className="date-picker-calendar-icon" />
              </div>

              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'captureSchedTargetDate123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Target Date:'}
                />
              )}
            </div>
            <div className="error-message">
              {targetDateError && <div className="small-red-text">{targetDateError}</div>}
            </div>
            <div className={'capture-visit-modal-button-container'}>
              <SaveButton
                textColor={'#6f8594'}
                border={'1px solid #6f8594'}
                title={'Cancel'}
                onClick={handleCancelScheduleChangeClick}
                color={'#ffffff'}
              />
              <SaveButton
                textColor={'#ffffff'}
                border={'1px solid #6f8594'}
                title={'Save'}
                onClick={handleSaveScheduleChangeClick}
                color={'#6f8594'}
              />
            </div>
          </div>
        </Modal>
      )}

      {isChangeVisitModalOpen && (
        <Modal
          closeModal={() => setIsChangeVisitModalOpen(false)}
          setIsModalOpen={setIsChangeVisitModalOpen}
          hideButton={true}
          headerTitle={'Add Visit'}
          width={'500px'}
          justifyContent={'center'}
          contentPadding={'2px'}
        >
          <div className="capture-visit-add-visit-modal">
            <div className="input-container" style={{ marginTop: '10px' }}>
              <label className={'input-label'}>Link Visit Code To:</label>
              <Select
                options={visitCodeList}
                closeMenuOnSelect={true}
                onChange={handleLinkVisitCodeToChange}
                value={visitCodeList.find((option) => option.value === newVisitCode)}
                classNamePrefix={`input-select${newVisitCodeError ? 'error-border' : ''}`}
                getOptionLabel={(option) => option.value.visitCode}
                getOptionValue={(option) => option.value}
              />
            </div>
            <div className="error-message">
              {newVisitCodeError && <div className="small-red-text">{newVisitCodeError}</div>}
            </div>
            <div className="input-container">
              <label className="input-label">Sub-Visit Code: </label>
              <input
                className={`text-input ${newSubVisitCodeError && 'error-border'}`}
                type="text"
                value={newSubVisitCode}
                onChange={({ target }) => {
                  setNewSubVisitCode(target.value);
                }}
              />
            </div>
            <div className="error-message">
              {newSubVisitCodeError && <div className="small-red-text">{newSubVisitCodeError}</div>}
            </div>
            <div className="input-container">
              <label className="input-label">New Visit Type:</label>
              <CreatableSelect
                isClearable
                value={visitTypeList.find((option) => option.value === newVisitType)}
                onChange={handleVisitTypeChange}
                options={visitTypeList}
                classNamePrefix={`input-select${newVisitTypeError ? 'error-border' : ''}`}
              />
            </div>
            <div className="error-message">
              {newVisitTypeError && <div className="small-red-text">{newVisitTypeError}</div>}
            </div>
            <div className={'capture-visit-modal-button-container'}>
              <SaveButton
                textColor={'#6f8594'}
                border={'1px solid #6f8594'}
                title={'Cancel'}
                onClick={handleCancelVisitChangeClick}
                color={'#ffffff'}
              />
              <SaveButton
                textColor={'#ffffff'}
                border={'1px solid #6f8594'}
                title={'Save'}
                onClick={handleSaveVisitChangeClick}
                color={'#6f8594'}
              />
            </div>
          </div>
        </Modal>
      )}

      {isAddFormModalOpen && (
        <Modal
          closeModal={closeFormsModal}
          setIsModalOpen={setIsAddFormModalOpen}
          headerTitle={'Add Forms'}
          hideButton={true}
          width={'55%'}
          height={'75%'}
          justifyContent={'center'}
          contentPadding={'10px'}
        >
          <ParticipantAddForms
            setFormList={setFormsList}
            formList={formsList}
            closeModal={closeFormsModal}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            context={context}
            studyExternalReference={participant?.activeStudyExternalReference}
          />
        </Modal>
      )}

      <ChangeParticipantScheduleConfirmation
        show={isScheduleConfirmationOpen}
        onConfirmClick={handleConfirmScheduleChange}
        onCancelClick={handleCancelScheduleConfirmation}
      />

      <AdjustVisitsWarning
        participant={participant}
        show={showAdjustVisitWarning}
        onAdjustDateClick={handleAdjustVisitsClick}
        onKeepDateClick={handleKeepVisitsClick}
        onCancelClick={handleCancelAdjustVisitClick}
      />

      <MissedVisitWarning
        participant={participant}
        show={showMissedVisitWarning}
        onLogMissedVisitClick={handleLogMissedVisitClick}
        onLogVisitLaterClick={handleLogVisitLaterClick}
        onLogVisitNowClick={handleLogVisitNowClick}
      />

      <MissedVisitCapture
        context={context}
        participant={participant}
        show={showMissedVisitCapture}
        onCancelClick={handleCancelMissedVisitClick}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        retrieveParticipant={retrieveParticipant}
        close={() => setShowMissedVisitCapture(false)}
      />
    </div>
  );
};
